import type { Alert, Improvements, SelectedSources, Site } from '@aloft/toolkit-shared';
import {
  isExplicitUnknownSelectedForFieldPath,
  schAloftArea,
  schAloftPrice,
  schBasementAtticFinishTypes,
  schPairedCostRouteItem,
  schSiteRating,
  schSixTierQuality,
  syntaxFNMA,
} from '@aloft/toolkit-shared';

import { z } from 'zod';

import type { PropertyGridCellKey } from '../../../../components/PropertyGrid/types.ts';
import type {
  CompPropertyDataState,
  PropertyDataState,
  SubjectPropertyDataState,
} from '../../../../redux/types.ts';
import type { AlertDefinition } from '../../types.ts';
import { getAlertTriggers } from '../utils/alertTriggers.ts';

const schParkingSpaces = z
  .object({
    spaces: z.number().int().nonnegative().optional(),
    hasFeature: z.boolean(),
  })
  .optional()
  .superRefine((data, ctx) => {
    if (data?.hasFeature && data?.spaces === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Required',
        path: ['spaces'],
      });
    }
  });

const schBasementFields = z
  .object({
    hasFeature: z.boolean(),
    type: schBasementAtticFinishTypes.optional(),
    totalArea: schAloftArea.optional(),
    finishedArea: schAloftArea.optional(),
    fullBaths: z.number().int().nonnegative().optional(),
    halfBaths: z.number().int().nonnegative().optional(),
  })
  .optional()
  .superRefine((data, ctx) => {
    if (!data?.hasFeature) return;
    (['type', 'totalArea', 'finishedArea', 'fullBaths', 'halfBaths'] as const).forEach((field) => {
      if (data[field] === undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Required',
          path: [field],
        });
      }
    });
  });

const schSalesComparablesImprovementFields = z.object({
  gla: schAloftArea,
  bedrooms: z.number().int().nonnegative(),
  fullBaths: z.number().int().nonnegative(),
  halfBaths: z.number().int().nonnegative(),
  age: z.number().int().nonnegative(),
  uadQuality: schSixTierQuality,
  uadCondition: schSixTierQuality,
  garage: schParkingSpaces,
  detachedGarage: schParkingSpaces,
  carport: schParkingSpaces,
  basement: schBasementFields,
});

const schSalesComparablesFields = z.object({
  id: z.string(),
  transaction: z
    .object({
      status: z.string().optional(),
      price: schAloftPrice.optional(),
      saleDate: syntaxFNMA.schIsoMonth.optional(),
      priceUnknown: z.boolean(),
      saleDateUnknown: z.boolean(),
    })
    .superRefine((data, ctx) => {
      if (data.price === undefined && !data.priceUnknown) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Required',
          path: ['price'],
        });
      }
      const saleDateRequired = !data.status || data.status === 'SettledSale';
      if (saleDateRequired && data.saleDate === undefined && !data.saleDateUnknown) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Required',
          path: ['saleDate'],
        });
      }
    }),
  site: z.object({
    area: schAloftArea,
    influence: z.string(),
    influenceRating: schSiteRating,
    view: z.string(),
    viewRating: schSiteRating,
  }),
  improvements: schSalesComparablesImprovementFields.extend({
    arv: schSalesComparablesImprovementFields.optional(),
  }),
});

const GARAGE_FIELD_PRIORITY = ['garage', 'detachedGarage', 'carport'] as const;

const issueToCellKey = (id: string, issue: z.ZodIssue): PropertyGridCellKey | undefined => {
  const path = issue.path as (string | number)[];
  if (!path.length) return undefined;
  return `${id}-editableData.${path.join('.')}` as PropertyGridCellKey;
};

const getTargetCellKeys = (id: string, issues: z.ZodIssue[]): PropertyGridCellKey[] => [
  ...new Set(
    issues
      .map((issue) => issueToCellKey(id, issue))
      .filter((key): key is PropertyGridCellKey => key !== undefined),
  ),
];

const getFocusCellKey = (id: string, issues: z.ZodIssue[]): PropertyGridCellKey | undefined => {
  const garageField = GARAGE_FIELD_PRIORITY.find((field) =>
    issues.some(
      (issue) =>
        issue.path[0] === 'improvements' && issue.path[1] === field && issue.path[2] === 'spaces',
    ),
  );
  if (garageField) {
    return `${id}-editableData.improvements.${garageField}.spaces` as PropertyGridCellKey;
  }
  return getTargetCellKeys(id, issues)[0];
};

const getSelectedSources = (propertyDataEntity: PropertyDataState): SelectedSources | undefined =>
  propertyDataEntity.id === 'subject'
    ? (propertyDataEntity as SubjectPropertyDataState).subjectData.selectedSources
    : (propertyDataEntity as CompPropertyDataState).compData.selectedSources;

const mapTransactionFields = (
  editableData: { transaction?: { status?: string; price?: unknown; saleDate?: string } },
  selectedSources?: SelectedSources,
) => ({
  status: editableData.transaction?.status,
  price: editableData.transaction?.price,
  saleDate: editableData.transaction?.saleDate,
  priceUnknown: isExplicitUnknownSelectedForFieldPath(
    selectedSources,
    'editableData.transaction.price',
  ),
  saleDateUnknown: isExplicitUnknownSelectedForFieldPath(
    selectedSources,
    'editableData.transaction.saleDate',
  ),
});

const mapImprovementFields = (improvements?: Improvements) => ({
  gla: improvements?.gla,
  bedrooms: improvements?.bedrooms,
  fullBaths: improvements?.fullBaths,
  halfBaths: improvements?.halfBaths,
  age: improvements?.age,
  uadQuality: improvements?.uadQuality,
  uadCondition: improvements?.uadCondition,
  garage: improvements?.garage,
  detachedGarage: improvements?.detachedGarage,
  carport: improvements?.carport,
  basement: improvements?.basement,
});

const mapSiteFields = (site?: Site) => ({
  area: site?.area,
  influence: site?.influence,
  influenceRating: site?.influenceRating,
  view: site?.view,
  viewRating: site?.viewRating,
});

const mapPropertyDataEntityToSalesComparableFieldsSchema = (
  propertyDataEntity: PropertyDataState,
) => {
  const selectedSources = getSelectedSources(propertyDataEntity);

  if (propertyDataEntity.id === 'subject') {
    const { editableData } = (propertyDataEntity as SubjectPropertyDataState).subjectData;
    const arvImprovements = editableData.arv
      ? {
          ...mapImprovementFields(editableData.arv.improvements),
          age: editableData.improvements?.age,
        }
      : undefined;

    return schSalesComparablesFields.omit({ transaction: true }).safeParse({
      id: propertyDataEntity.id,
      improvements: {
        ...mapImprovementFields(editableData.improvements),
        arv: arvImprovements,
      },
      site: mapSiteFields(editableData.site),
    });
  }

  const { editableData } = (propertyDataEntity as CompPropertyDataState).compData;

  return schSalesComparablesFields.safeParse({
    id: propertyDataEntity.id,
    transaction: mapTransactionFields(editableData, selectedSources),
    improvements: mapImprovementFields(editableData.improvements),
    site: mapSiteFields(editableData.site),
  });
};

export const missingPropertyData: AlertDefinition = ({
  propertyDataEntities,
  compSequence,
  propertyType,
  compType,
  fullCompSequence,
  order,
}) => {
  const triggers = getAlertTriggers({
    fixRoute: schPairedCostRouteItem.enum.features,
    order,
  });

  return ['subject', ...compSequence]
    .map((id) => {
      if (propertyType && id === 'subject' && propertyType !== 'subject') return;
      if (propertyType && id !== 'subject' && propertyType !== 'comp') return;

      const propertyDataEntity = propertyDataEntities[id];
      if (!propertyDataEntity) return;

      const result = mapPropertyDataEntityToSalesComparableFieldsSchema(propertyDataEntity);
      if (result.success) return;

      const propertyName =
        id === 'subject' ? 'Subject' : `Comp ${(fullCompSequence?.indexOf(id) ?? 0) + 1}`;

      const targetCellKey = getFocusCellKey(id, result.error.issues);
      const targetCellKeys = getTargetCellKeys(id, result.error.issues);

      return {
        type: 'missingPropertyData',
        status: 'active',
        severity: 'error',
        orderType: ['appraisify'],
        message: `${propertyName} has missing data.`,
        propertyID: id,
        ...(compType && { compType }),
        fixingInfo: {
          tips: 'One or more of the rows for the property is incomplete.',
          pairedCostRouteItem: 'features',
          fixingMethod: ['action'],
          commentAlwaysRequired: false,
          targetCellKey,
          targetCellKeys,
        },
        triggers,
      } as Alert;
    })
    .filter((alert): alert is Alert => alert !== undefined);
};
